import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

import DadosUsuario from '../utils/context';
import { getSectionKeyByPath, getSectionPath, getVisibleSections } from '../utils/menuSections';

const expandedWidth = 248;
const collapsedWidth = 76;

const itemSx = (active) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '12px',
  mx: 1,
  mb: 0.5,
  minHeight: 42,
  pl: 1.75,
  color: active ? 'var(--app-sidebar-active-text)' : 'var(--app-muted)',
  bgcolor: active ? 'var(--app-sidebar-active-bg)' : 'transparent',
  transition: 'background-color 0.18s ease, color 0.18s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '9px auto 9px 0',
    width: 4,
    borderRadius: '0 4px 4px 0',
    backgroundColor: active ? 'var(--ambar)' : 'transparent'
  },
  '&.Mui-selected': {
    bgcolor: 'var(--app-sidebar-active-bg)',
    color: 'var(--app-sidebar-active-text)'
  },
  '&.Mui-selected:hover': {
    bgcolor: 'var(--app-sidebar-active-hover-bg)'
  },
  '&:hover': {
    bgcolor: active ? 'var(--app-sidebar-active-hover-bg)' : 'var(--app-surface)',
    color: 'var(--app-text)'
  }
});

const labelProps = {
  fontFamily: 'var(--app-font-body)',
  fontSize: '0.88rem',
  fontWeight: 600,
  noWrap: true
};

function Sidebar () {
  const location = useLocation();
  const navigate = useNavigate();
  const { autorizacoes = [] } = useContext(DadosUsuario);
  const [collapsed, setCollapsed] = useState(false);

  const visibleSections = useMemo(() => getVisibleSections(autorizacoes), [autorizacoes]);

  const selectedSection = useMemo(() => getSectionKeyByPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const offset = collapsed ? collapsedWidth : expandedWidth;
    document.documentElement.style.setProperty('--drawer-offset', `${offset}px`);

    return () => {
      document.documentElement.style.setProperty('--drawer-offset', '0px');
    };
  }, [collapsed]);

  return (
    <Drawer
      anchor='left'
      open
      variant='permanent'
      sx={{
        width: collapsed ? collapsedWidth : expandedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          top: 'var(--app-topbar-height)',
          height: 'calc(100% - var(--app-topbar-height))',
          width: collapsed ? collapsedWidth : expandedWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid var(--app-line)',
          background: 'var(--app-sidebar-bg)',
          color: 'var(--app-text)',
          transition: 'width 0.25s ease'
        }
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction='row' alignItems='center' justifyContent={collapsed ? 'center' : 'space-between'} sx={{ px: 1.5, py: 1.25 }}>
          {!collapsed && <Typography className='ui-eyebrow' component='span' sx={{ pl: 0.5 }}>Menu</Typography>}
          <IconButton size='small' aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'} onClick={() => setCollapsed((prev) => !prev)}>
            {collapsed ? <MenuRoundedIcon fontSize='small' /> : <MenuOpenRoundedIcon fontSize='small' />}
          </IconButton>
        </Stack>

        <List sx={{ pt: 0 }}>
          <Tooltip title={collapsed ? 'Home' : ''} placement='right'>
            <ListItemButton selected={selectedSection === 'home'} onClick={() => navigate('/')} sx={itemSx(selectedSection === 'home')}>
              <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}><HomeRoundedIcon fontSize='small' /></ListItemIcon>
              {!collapsed && <ListItemText primary='Home' primaryTypographyProps={labelProps} />}
            </ListItemButton>
          </Tooltip>

          {visibleSections.map((section) => {
            const Icon = section.icon;
            const active = selectedSection === section.key;
            return (
              <Tooltip key={section.key} title={collapsed ? section.title : ''} placement='right'>
                <ListItemButton onClick={() => navigate(getSectionPath(section))} selected={active} sx={itemSx(active)}>
                  <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}><Icon fontSize='small' /></ListItemIcon>
                  {!collapsed && <ListItemText primary={section.title} primaryTypographyProps={labelProps} />}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Stack>
    </Drawer>
  );
}

export default Sidebar;
